import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useConfirmDialog,
  TableRowActionsMenu,
  ConfirmDialog,
  Typography,
} from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import { useOfferDeleteMutation } from '@occc-app/dataAccessLayer/offer/mutations';

import { routeCreator } from '../utils';

type AwardCatalogActionsContainerProps = {
  id: string;
  name: string;
  deleted: boolean;
  isUserPermittedToEditAward: boolean;
  isUserPermittedToDeleteAward: boolean;
};

export const AwardCatalogActionsContainer = ({
  id,
  name,
  deleted,
  isUserPermittedToEditAward,
  isUserPermittedToDeleteAward,
}: AwardCatalogActionsContainerProps) => {
  const navigate = useNavigate();

  const onDeleteCompleted = () => snackbar.success('Award has been successfully archived.');

  const [offerDeleteMutation] = useOfferDeleteMutation({
    onCompleted: onDeleteCompleted,
    refetchQueries: ['awardsList'],
  });

  const onDelete = () => {
    offerDeleteMutation({
      variables: { id },
    });
  };

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: onDelete,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isUserPermittedToEditAward) {
      items.push({
        label: 'Edit Award',
        onClick: () => navigate(routeCreator.update(id)),
        dataTestId: 'edit',
      });
    }

    if (isUserPermittedToDeleteAward && !deleted) {
      items.push({
        label: 'Archive Award',
        onClick: () => setOpen(true),
        dataTestId: 'archive',
      });
    }

    return items;
  }, [deleted, id, navigate, setOpen, isUserPermittedToEditAward, isUserPermittedToDeleteAward]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} />
      <ConfirmDialog
        dialogTitle='Are you sure?'
        open={open}
        onClose={onCloseButtonClick}
        confirmButtonText='Confirm'
        confirmButtonProps={{
          onClick: onConfirmButtonClick,
          color: 'error',
        }}
      >
        <Typography>This action will archive this award: "{name}".</Typography>
      </ConfirmDialog>
    </>
  );
};
